<script>
    import TutorialItem from "./TutorialItem.svelte";
</script>

<section class="tutorial">
    <h1 class="tutorial__title">About DataJam</h1>
    <p class="tutorial__text">
        With the help of the DataJam service, you can see which accounts and photos the user liked or commented on, which accounts he followed and unfollowed, as well as a variety of other data. Since Instagram itself disabled the function of viewing likes from friends, DataJam is now the only optimal solution to quickly analyze all likes. Additionally, you can collect the entire archive of the actions of the desired user or organize constant tracking of his activity.
    </p>
    <div class="tutorial__wrapper">

        <TutorialItem>
            <span slot="title">Tracking start</span>
            <div slot="content">
                <TutorialItem isChild={true}>
                    <span slot="title">Adding a new Target</span>
                    <div slot="content">
                        <div class="tutorial-subsection__body">
                            <p class="tutorial-subsection__text">
                                To add an account you want to track (hereinafter referred to as the "Target"), proceed to the "Add an account" section, which is located in the Menu as well as on the Dashboard page
                            </p>
                            <div class="tutorial-subsection__img-wrapper">
                                <picture>
                                    <source srcset="/images/spy-tutorial/en-US/01.webp" type="image/webp">
                                    <source srcset="/images/spy-tutorial/en-US/01.png" type="image/png">
                                    <img src="/images/spy-tutorial/en-US/01.png" alt="" class="tutorial-subsection__image" />
                                </picture>
                            </div>
                            <div class="tutorial-subsection__img-wrapper">
                                <picture>
                                    <source srcset="/images/spy-tutorial/en-US/02.webp" type="image/webp">
                                    <source srcset="/images/spy-tutorial/en-US/02.png" type="image/png">
                                    <img src="/images/spy-tutorial/en-US/02.png" alt="" class="tutorial-subsection__image" />
                                </picture>
                            </div>
                            <p class="tutorial-subsection__text">
                                After that, you will be taken to the Adding a new target page. Here, in the provided field, you need to type in the login of your Target, making DataJam automatically show the search result among all Instagram users. You only need to select the desired account and click on it.
                            </p>
                        </div>
                    </div>
                </TutorialItem>

                <TutorialItem isChild={true}>
                    <span slot="title">Multiple Targets analysis</span>
                    <div slot="content">
                        <div class="tutorial-subsection__body">
                            <p class="tutorial-subsection__text">
                                Using a standard plan, you can add up to 3 Targets for tracking.
                                When tracking multiple Targets, you can switch between them by clicking the arrows next to the Target information panel
                            </p>
                        </div>
                    </div>
                </TutorialItem>

                <TutorialItem isChild={true}>
                    <span slot="title">Tracking a private account</span>
                    <div slot="content">
                        <div class="tutorial-subsection__body">
                            <p class="tutorial-subsection__text">
                                You can add a private account as a Target. At the stage of adding an account, you will receive a message informing you of the necessity to add an account following the Target. That means activity monitoring will be conducted through an account following your Target.
                            </p>
                            <div class="tutorial-subsection__img-wrapper">
                                <picture>
                                    <source srcset="/images/spy-tutorial/en-US/03.webp" type="image/webp">
                                    <source srcset="/images/spy-tutorial/en-US/03.png" type="image/png">
                                    <img src="/images/spy-tutorial/en-US/03.png" alt="" class="tutorial-subsection__image" />
                                </picture>
                            </div>
                            <p class="tutorial-subsection__text">
                                If you can't follow the Target account with your own account for some reason, try registering a fake account in order to follow your Target. Connect the fake account to DataJam.
                                Please note that the account to which you are connecting must have two-factor authentication disabled.
                            </p>
                        </div>
                    </div>
                </TutorialItem>

                <TutorialItem isChild={true}>
                    <span slot="title">Analysis results</span>
                    <div slot="content">
                        <div class="tutorial-subsection__body">
                            <p class="tutorial-subsection__text">
                                After starting to track your Target, DataJam will need some time to collect primary information from your Target's page (it usually takes from a few minutes to several hours, depending on the size of the Target account).
                                Every day since then, you are going to receive updated information about all the actions concerning your Target's account. Important: Don't forget to update the data by manually running the next analysis.
                                The information received about your Target is divided into categories:
                            </p>

                            <p class="tutorial-subsection__text">
                                Each category contains relevant events. Let us tell you more about them:
                            </p>
                            <div class="tutorial-subsection__text">
                                <ul>
                                    <li>Likes: this category contains information about the likes received and put.</li>
                                    <li>Comments: this category contains information about comments received and left.</li>
                                    <li>Stories: contains information on stories published by your Target.</li>
                                    <li>Following: new subscriptions of your Target - the accounts he (or she) followed.</li>
                                    <li>Followers: new subscribers of your target - those who followed him (or her)</li>
                                    <li>Posts and Reels: new publications of your Target.</li>
                                    <li>Secret admirers: the users who interact with your Target's page, but do not follow the Target account.</li>
                                    <li>In order for DataJam to continue collecting data on your Target, click the 'Update data' button after the data collection cycle (24 hours) has completed.</li>
                                </ul>
                            </div>
                            <div class="tutorial-subsection__img-wrapper">
                                <picture>
                                    <source srcset="/images/spy-tutorial/en-US/04.webp" type="image/webp">
                                    <source srcset="/images/spy-tutorial/en-US/04.png" type="image/png">
                                    <img src="/images/spy-tutorial/en-US/04.png" alt="" class="tutorial-subsection__image" />
                                </picture>
                            </div>
                        </div>
                    </div>
                </TutorialItem>

                <TutorialItem isChild={true}>
                    <span slot="title">User verification</span>
                    <div slot="content">
                        <div class="tutorial-subsection__body">
                            <p class="tutorial-subsection__text">
                                Verification is the process of confirming the user's access to the account, so Instagram* can make sure that you have access to the account and that the actions are performed by you.
                            </p>
                            <p class="tutorial-subsection__text">
                                For DataJam users, verification can only occur when adding your own account to analyze a private account.
                            </p>
                            <p class="tutorial-subsection__text">
                                Instagram* will send you a verification code to your email or phone number that is linked to your Instagram* account in order to secure your account.
                            </p>
                            <p class="tutorial-subsection__text">
                                Usually, verification occurs when an account is added to DataJam for the first time. In this case, a modal window will appear on the dashboard page informing you that verification is necessary (an email from our service will also be sent to you indicating that we encountered such a need).
                            </p>
                            <p class="tutorial-subsection__text">
                                During the service development, we also identified the following ways in which Instagram* counteracts promotion services (if you come across one of them, don't worry; these are regular situations; they are just a little more complicated than usual verification):
                            </p>
                            <div class="tutorial-subsection__text">
                                <ul>
                                    <li>Circular verifications (rarely): Instagram* will repeatedly ask you to enter the confirmation code, usually from 3 to 8 times, after which access will be restored and the service will continue working as usual.</li>
                                    <li>Changing Instagram password (quite rarely): there are cases when Instagram* asks the user to change the password on the instagram* account, as it suspects that intruders could have taken over your account.</li>
                                    <li>Selfie verification (extremely rare) is a new type of verification that has been noticed by us only a few times during the entire existence of the service, but it happens sometimes. Instagram * requests that you perform several head turns in front of the selfie camera of your smartphone. Instagram* does not store this data and does not record videos; it is necessary for the algorithms to make sure that a real person has access to the account.</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </TutorialItem>

                <TutorialItem isChild={true}>
                    <span slot="title">Targets managment</span>
                    <div slot="content">
                        <div class="tutorial-subsection__body">
                            <p class="tutorial-subsection__text">
                                To delete a target added for analysis - go to Settings, select the target you want to delete and click on the "Delete" button.
                            </p>
                            <p class="tutorial-subsection__text">
                                It is important to note that the target for which the analysis was launched can be deleted only 7 days after the first analysis has been launched.
                            </p>

                            <div class="tutorial-subsection__img-wrapper">
                                <picture>
                                    <source srcset="/images/spy-tutorial/en-US/05.webp" type="image/webp">
                                    <source srcset="/images/spy-tutorial/en-US/05.png" type="image/png">
                                    <img src="/images/spy-tutorial/en-US/05.png" alt="" class="tutorial-subsection__image" />
                                </picture>
                            </div>
                        </div>
                    </div>
                </TutorialItem>
            </div>
        </TutorialItem>

        <TutorialItem>
            <span slot="title">Plans and subscription</span>
            <div slot="content">
                <p class="tutorial-subsection__text">
                    You can cancel your subscription in the Subscription Management section. To do this, click on the 'Subscription management' button on the Plans page.
                </p>
                <p class="tutorial-subsection__text">
                    Your card data transmitted to the Payment system's server is encrypted. Payment security is also ensured by the PCI DSS international standard, which includes 12 requirements developed by Visa, MasterCard, and other payment systems.
                </p>

                <TutorialItem isChild={true}>
                    <span slot="title">7-day plan</span>
                    <div slot="content">
                        <div class="tutorial-subsection__body">
                            <p class="tutorial-subsection__text">
                                The plan is activated by default at the end of the trial period, providing 7 days of access and automatic renewal. As part of the plan, you will have full access to 3 targets for tracking.
                            </p>
                        </div>
                    </div>
                </TutorialItem>

                <TutorialItem isChild={true}>
                    <span slot="title">1-month plan</span>
                    <div slot="content">
                        <div class="tutorial-subsection__body">
                            <p class="tutorial-subsection__text">
                                The plan is activated by default at the end of the trial period, providing 7 days of access and automatic renewal. As part of the plan, you will have full access to 3 targets for tracking.
                            </p>
                        </div>
                    </div>
                </TutorialItem>

            </div>
        </TutorialItem>

        <TutorialItem>
            <span slot="title">Personal account interface</span>
            <div slot="content">
                <p class="tutorial-subsection__text">
                    This section will help you navigate the interface of your personal account and show where to find all the sections and functions you may need!
                </p>

                <TutorialItem isChild={true}>
                    <span slot="title">Main menu (header)</span>
                    <div slot="content">
                        <div class="tutorial-subsection__body">
                            <div class="tutorial-subsection__img-wrapper">
                                <picture>
                                    <source srcset="/images/spy-tutorial/en-US/06.webp" type="image/webp">
                                    <source srcset="/images/spy-tutorial/en-US/06.png" type="image/png">
                                    <img src="/images/spy-tutorial/en-US/06.png" alt="" class="tutorial-subsection__image" />
                                </picture>
                            </div>
                            <p class="tutorial-subsection__text">
                                Here you can find the following elements:
                            </p>
                            <ol>
                                <li>Back: The Back button will return you to the Dashboard if you are currently on one of the internal pages of the service.</li>
                                <li>The name of the current page shows your location.</li>
                                <li>Menu: You can navigate to additional sections from here:</li>
                            </ol>
                            <div class="tutorial-subsection__img-wrapper">
                                <picture>
                                    <source srcset="/images/spy-tutorial/en-US/07.webp" type="image/webp">
                                    <source srcset="/images/spy-tutorial/en-US/07.png" type="image/png">
                                    <img src="/images/spy-tutorial/en-US/07.png" alt="" class="tutorial-subsection__image" />
                                </picture>
                            </div>
                            <p class="tutorial-subsection__text">
                                Here you can find the following elements
                            </p>
                            <ol>
                                <li>Dashboard: Will return you to the Dashboard (Home page).</li>
                                <li>Add an account: Proceed to the page for adding a new Target,</li>
                                <li>Guide: Learn to use DataJam,</li>
                                <li>Plans and subscriptions: Payment page, plan selection, and subscription cancellation,</li>
                                <li>Technical support: Opens a widget or a page for contacting technical support for the service,</li>
                                <li>Notifications: Manage notifications on this page</li>
                                <li>Settings: The page where you can change your user info on DataJam and manage the added Targets,</li>
                                <li>Affiliate program: Proceed to registration in the partner cabinet,</li>
                                <li>Exit: really?</li>
                            </ol>
                        </div>
                    </div>
                </TutorialItem>

                <TutorialItem isChild={true}>
                    <span slot="title">Dashboard</span>
                    <div slot="content">
                        <div class="tutorial-subsection__body">
                            <div class="tutorial-subsection__img-wrapper">
                                <picture>
                                    <source srcset="/images/spy-tutorial/en-US/08.webp" type="image/webp">
                                    <source srcset="/images/spy-tutorial/en-US/08.png" type="image/png">
                                    <img src="/images/spy-tutorial/en-US/08.png" alt="" class="tutorial-subsection__image" />
                                </picture>
                            </div>

                            <ol>
                                <li>Target: displays the account, the data of which is currently presented,</li>
                                <li>The Target login and the Target data: the login of the account that is being tracked,</li>
                                <li>Change account: switch among the added accounts,</li>
                                <li>The Selected account data: shows the current data of the selected account,</li>
                                <li>The Update area: information about the last update made is shown here, and the 'Update data' button starts a new cycle of collecting information about the Target.</li>
                                <li>Information area: all received data on your target's activity is displayed here.</li>
                            </ol>
                        </div>
                    </div>
                </TutorialItem>

                <TutorialItem isChild={true}>
                    <span slot="title">Plans page</span>
                    <div slot="content">
                        <div class="tutorial-subsection__body">
                            <div class="tutorial-subsection__img-wrapper">
                                <picture>
                                    <source srcset="/images/spy-tutorial/en-US/09.webp" type="image/webp">
                                    <source srcset="/images/spy-tutorial/en-US/09.png" type="image/png">
                                    <img src="/images/spy-tutorial/en-US/09.png" alt="" class="tutorial-subsection__image" />
                                </picture>
                            </div>

                            <ol>
                                <li>Info section: Here you can see the number of Targets added, the day your plan is valid until, the name of the current plan, and also go to the Subscription management section.</li>
                                <li>List of Plans: Here, you can select the plan you are interested in as well as check your current plan.</li>
                                <li>Payment for a plan: in order to pay for a plan, you must first select the plan you are interested in by clicking the corresponding card.</li>
                            </ol>
                        </div>
                    </div>
                </TutorialItem>

            </div>
        </TutorialItem>

        <TutorialItem>
            <span slot="title">Notifications</span>
            <div slot="content">
                <p class="tutorial-subsection__text">
                    You can configure notifications about new events tracked by DataJam in the accounts of your Targets.
                </p>
                <p class="tutorial-subsection__text">
                    You can activate sending notifications on the <a href="/instashpion/dashboard/settings">Notifications</a> page.
                </p>

                <div class="tutorial-subsection__img-wrapper">
                    <picture>
                        <source srcset="/images/spy-tutorial/en-US/10.webp" type="image/webp">
                        <source srcset="/images/spy-tutorial/en-US/10.png" type="image/png">
                        <img src="/images/spy-tutorial/en-US/10.png" alt="" class="tutorial-subsection__image" />
                    </picture>
                </div>

                <p class="tutorial-subsection__text">
                    Here you can find the following elements:
                </p>
                <ol>
                    <li>Data availability: Activate the email notification informing you that the data is collected and the service is ready for a new analysis.</li>
                </ol>
            </div>
        </TutorialItem>

        <TutorialItem>
            <span slot="title">Additional info</span>
            <div slot="content">
                <p class="tutorial-subsection__text">
                    The DataJam service is being worked on by a large number of managers and developers. We are continually considering new concepts and doing tests on theories that are intended to make it easier for you, our Users, to interact with our services and receive the best functionality possible for your needs. The Guide may become out of date as a result of the numerous ongoing upgrades and improvements. While we make every effort to swiftly mark and record information about updates, we respectfully ask for your patience if you unexpectedly encounter something in the interface that is not covered in the Guide; most likely, we are already working on it.
                </p>
                <p class="tutorial-subsection__text">
                    This Guide version: June 1, 2023
                </p>
            </div>
        </TutorialItem>


    </div>
</section>

<style>
    picture img {
        max-width: 360px;
        border-radius: 12px;
        border: 1px solid #161516;
    }
</style>